/**
 * Homework Notification Dropdown
 *
 * Bell trigger that opens a dropdown (desktop) or drawer (mobile)
 * with the user's pending homework / submission list. Styling lives
 * in layout.css under .lms-notifs-*.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Dropdown, Drawer } from 'antd';
import { IconBell, IconHomework, IconPracticeTest, IconClose } from '../components/icons';
import { useTranslation } from 'react-i18next';

import { BREAKPOINTS } from '../../core/constants';
import { IconTile, EmptyState } from '../components/primitives';

const HomeworkNotificationDropdown = ({ notificationCount, homeworkList, role }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < BREAKPOINTS.MOBILE);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < BREAKPOINTS.MOBILE);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ≤1 day is the failure state (garnet), ≤3 the warning (saffron), later the
  // calm one (pistachio) — replacing Ant's red/orange/green presets. The day
  // count is always written, so colour is never the only carrier.
  const getDueTone = (daysLeft) => {
    if (daysLeft <= 1) return 'garnet';
    if (daysLeft <= 3) return 'saffron';
    return 'pistachio';
  };

  const fullListPath = role === 'TEACHER' ? '/teacher/practice-tests' : '/student/subjects';

  const handleItemClick = (item) => {
    if (isMobile) setDrawerVisible(false);
    setDropdownOpen(false);
    if (role === 'TEACHER') {
      navigate('/teacher/practice-tests');
    } else {
      navigate(`/student/subjects/${item.groupId}/tasks`, { state: { record: item } });
    }
  };

  const handleBellClick = () => {
    if (isMobile && (role === 'STUDENT' || role === 'TEACHER')) {
      setDrawerVisible(true);
    }
  };

  const goToFullList = () => {
    setDrawerVisible(false);
    setDropdownOpen(false);
    navigate(fullListPath);
  };

  const notificationTitle =
    role === 'TEACHER' ? t('common.pendingSubmissions') : t('common.upcomingHomework');
  const emptyText =
    role === 'TEACHER' ? t('common.noPendingSubmissions') : t('common.noPendingHomework');
  const emptyActionLabel =
    role === 'TEACHER' ? t('practiceTests.title') : t('navigation.courses');

  const renderRow = (item) => {
    if (role === 'TEACHER') {
      return (
        <button
          type="button"
          key={item.testId}
          className="lms-notifs__row"
          onClick={() => handleItemClick(item)}
        >
          <div className="lms-notifs__row-main">
            <IconTile icon={<IconPracticeTest />} tone="plum" size="sm" />
            <span className="lms-notifs__row-title">{item.testTitle}</span>
            {item.pendingCount ? (
              <span className="lms-notifs__due lms-notifs__due--plum">{item.pendingCount}</span>
            ) : null}
          </div>
          <span className="lms-notifs__row-sub">
            {[item.groupName, item.submittedAt].filter(Boolean).join(' · ')}
          </span>
        </button>
      );
    }
    const tone = getDueTone(item.daysLeft);
    return (
      <button
        type="button"
        key={`${item.taskName}-${item.deadline}`}
        className={`lms-notifs__row ${item.daysLeft <= 1 ? 'lms-notifs__row--urgent' : ''}`}
        onClick={() => handleItemClick(item)}
      >
        <div className="lms-notifs__row-main">
          <IconTile icon={<IconHomework />} subject={item.courseName} size="sm" />
          <span className="lms-notifs__row-title">{item.taskName}</span>
          <span className={`lms-notifs__due lms-notifs__due--${tone}`}>{item.daysLeft}d</span>
        </div>
        <span className="lms-notifs__row-sub">
          {[item.courseName, item.groupName, new Date(item.deadline).toLocaleDateString()]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </button>
    );
  };

  const emptyBlock = (
    <div className="lms-notifs__empty">
      <EmptyState
        icon={<IconHomework />}
        tone="success"
        title={emptyText}
        action={
          <Button type="primary" onClick={goToFullList}>
            {emptyActionLabel}
          </Button>
        }
      />
    </div>
  );

  const list = (
    <div className="lms-notifs__list">
      {homeworkList.length === 0 ? emptyBlock : homeworkList.map(renderRow)}
    </div>
  );

  const footer =
    homeworkList.length > 0 ? (
      <button type="button" className="lms-notifs__foot" onClick={goToFullList}>
        {t('common.viewAll')}
      </button>
    ) : null;

  const dropdownContent = (
    <div className="lms-notifs">
      <div className="lms-notifs__head">
        <span className="lms-notifs__head-title">{notificationTitle}</span>
        {notificationCount > 0 && (
          <span className="lms-notifs__head-count">{notificationCount}</span>
        )}
      </div>
      {list}
      {footer}
    </div>
  );

  if (role !== 'STUDENT' && role !== 'TEACHER') {
    return (
      <Badge count={0} showZero={false} size="small" offset={[-6, 6]}>
        <Button
          type="text"
          icon={<IconBell />}
          className="lms-header__icon-btn"
          aria-label={notificationTitle}
        />
      </Badge>
    );
  }

  const bellLabel = `${notificationTitle}${notificationCount > 0 ? ` (${notificationCount})` : ''}`;

  return (
    <>
      {isMobile ? (
        <>
          <Badge count={notificationCount} showZero={false} size="small" offset={[-6, 6]}>
            <Button
              type="text"
              icon={<IconBell />}
              className={`lms-header__icon-btn ${drawerVisible ? 'is-open' : ''}`}
              onClick={handleBellClick}
              aria-label={bellLabel}
            />
          </Badge>
          <Drawer
            title={
              <div className="lms-notifs__drawer-title">
                <span>{notificationTitle}</span>
                {notificationCount > 0 && (
                  <span className="lms-notifs__head-count">{notificationCount}</span>
                )}
              </div>
            }
            placement="right"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            width="85%"
            styles={{ body: { padding: 0 } }}
            closeIcon={<IconClose />}
            footer={footer}
          >
            {list}
          </Drawer>
        </>
      ) : (
        <Dropdown
          dropdownRender={() => dropdownContent}
          trigger={['click']}
          placement="bottomRight"
          arrow={{ pointAtCenter: true }}
          open={dropdownOpen}
          onOpenChange={setDropdownOpen}
        >
          <Badge count={notificationCount} showZero={false} size="small" offset={[-6, 6]}>
            <Button
              type="text"
              icon={<IconBell />}
              className={`lms-header__icon-btn ${dropdownOpen ? 'is-open' : ''}`}
              aria-label={bellLabel}
            />
          </Badge>
        </Dropdown>
      )}
    </>
  );
};

export default HomeworkNotificationDropdown;
